import {
  Box,
  styled,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import { cloneDeep } from "lodash";
import { mediaQueries } from "../../theme/breakpoints";
import colors from "../../theme/colors";
import fonts from "../../theme/fonts";
import { HalvesGameState, ROUND_TYPE } from "../../types/halves";
import { BaseMatchState } from "../../types/match";

interface HalvesTableProps {
  matchState: BaseMatchState;
}

export default function HalvesTable({ matchState }: HalvesTableProps) {
  const { completed } = matchState;
  const lastLeg = completed.legs[completed.legs.length - 1];
  const lastSetsLeg =
    completed.sets[completed.sets.length - 1]?.legs[
      completed.sets[completed.sets.length - 1].legs.length - 1
    ];
  const game = lastLeg ?? lastSetsLeg;
  const { rounds, winner, players } = game as HalvesGameState;
  const sortedPlayers = cloneDeep(players);
  sortedPlayers.sort((a, b) => b.score - a.score);

  return winner ? (
    <Wrapper>
      <Box display="flex" justifyContent="space-around">
        {sortedPlayers.map((p, i) => (
          <PlayerScore winner={i === 0 || p.score === sortedPlayers[0].score}>
            <Box>{p.name}</Box>
            <Box>{p.score}</Box>
          </PlayerScore>
        ))}
      </Box>
      <Box width="100%">
        <TableContainer
          style={{ backgroundColor: colors.lightBeige, color: colors.darkGray }}
          component={Paper}
        >
          <Table size="small" aria-label="Halves It results table">
            <TableHead style={{ fontSize: fonts.size.large }}>
              <TableRow>
                <HeaderCell align="center">Round</HeaderCell>
                {players.map((p) => (
                  <HeaderCell key={p.name}>{p.name}</HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rounds.map((r, i) => {
                return (
                  <TableRow>
                    <DataCell>
                      {r.type === ROUND_TYPE.SEL_NUMBER
                        ? `${r.number}s`
                        : r.type}
                    </DataCell>
                    {players.map((p, j) => {
                      const round = p.pastRounds[i];
                      return (
                        <DataCell key={`${p.name}_${r.type}`}>
                          {round.success ? round.score : "½"}{" "}
                          <Box component="span" fontSize={fonts.size.small}>
                            ({round.endingScore})
                          </Box>
                        </DataCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Wrapper>
  ) : null;
}

const Wrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  width: "65vw",
  margin: "auto",
  [mediaQueries.tablet]: {
    width: "100%",
  },
  [mediaQueries.mobile]: {
    width: "95%",
  },
});

const PlayerScore = styled(Box)<{ winner: boolean }>(({ winner }) => ({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
  fontSize: `${winner ? fonts.size.extraLarge : fonts.size.large}`,
  [mediaQueries.tablet]: {
    fontSize: `${winner ? fonts.size.large : fonts.size.medium}`,
  },
  [mediaQueries.mobile]: {
    fontSize: `${winner ? fonts.size.large : fonts.size.medium}`,
  },
}));

const BaseCell = styled(TableCell)({
  textAlign: "center",
  borderColor: colors.darkBeige,
});

const HeaderCell = styled(BaseCell)({
  fontSize: fonts.size.mediumLarge,
});

const DataCell = styled(BaseCell)({
  fontSize: fonts.size.medium,
});
